import {
    ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import {DateTime} from 'luxon';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {DxPopupComponent} from 'devextreme-angular/ui/popup';
import {DxChartComponent} from 'devextreme-angular/ui/chart';
import {MessageBusService} from '../message-bus.service';
import {ShellClientService} from '../shell-communication/shell-client.service';
import {OptionsChainService} from '../option-chains.service';
import {DetectMethodChanges, daysToExpiration, isValidNumber} from '../utils';
import {
    GetHorizontalSkewCurve,
    GetHorizontalSkewCurveReply,
    HorizontalSkewBasis,
    HorizontalSkewCurvePoint
} from './horizontal-skew-cqrs.model';

// Shares the CR-20260701-001 temporary diagnostic switch; stripped at R7 cleanup.
const HS_TRACE = true;

// Window-length cap — must match the server-side cap in HorizontalSkewService.CurveMaxDays.
const CURVE_MAX_DAYS = 120;

/** One selectable expiration in the window pickers. */
interface WindowOption {
    value: string;   // optionExpirationDate, "yyyy-MM-dd" — the wire value
    label: string;   // dropdown display, e.g. "Jul 31 (30d)"
    short: string;   // compact axis label, e.g. "Jul 31"
    dte: number;     // days to expiration (for ordering + the +120d cap)
}

/** One plotted term-structure point fed to dx-chart. */
interface ChartPoint {
    exp: string;      // "yyyy-MM-dd"
    expLabel: string; // discrete argument-axis category (compact date)
    dte: number;      // days to expiration (tooltip)
    ivPct: number;    // ATM IV as a percentage (value axis)
}

/**
 * Standalone, reusable Horizontal-Skew term-structure chart (CR-20260701-001, R2).
 *
 * Opened on-demand by a chart-icon button on any H-Skew surface (the SPX tray now; each
 * OPG panel later, R3). It is NOT integrated with the surface's reading overlay — it is a
 * separate popup that renders the full IV curve across a chosen expiration window.
 *
 * It is SEEDED with the surface's current basis (passed into `open`) but also carries its own
 * Spot/Forward toggle, so the user can compare bases without leaving the chart. Data is a
 * one-shot `GetHorizontalSkewCurve` snapshot (not the live subscription), drawn with a
 * DevExtreme dx-chart line series (native granular axis labels + mouse zoom/pan).
 *
 * Owner UX (2026-07-01): title bar reads just "Horizontal Skew"; the popup uses the DEFAULT
 * dx-popup title bar (so it drags), opens BLANK (the user picks a window and presses Plot — no
 * auto-fetch), carries a Spot/Forward toggle (Co-Pilot accent on the selected segment), and
 * shows "<underlying> - term structure (<N> days)" as a short legend BELOW the chart, where N is
 * the selected window span (difference of the two legs' days-to-expiration).
 */
@Component({
    selector: 'ets-horizontal-skew-chart-popup',
    templateUrl: './horizontal-skew-chart-popup.component.html',
    styleUrls: ['./horizontal-skew-chart-popup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HorizontalSkewChartPopupComponent implements OnInit, OnDestroy {

    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _shellClient: ShellClientService,
        private readonly _optionChains: OptionsChainService,
        private readonly _messageBus: MessageBusService,
    ) {
    }

    private readonly _unsubscriber = new Subject<void>();

    ngOnInit(): void {
        // ETS-standard reference-data refresh (same pattern as the expiration-selector and the
        // OPG pad): QG cursor/jump moves update the cached chains' day counts in place and
        // publish 'Qg.ChainsUpdatedMessage' — an OPEN popup re-binds its pickers to the new
        // world (a closed one reloads on the next open()).
        this._messageBus.of<any>('Qg.ChainsUpdatedMessage')
            .pipe(takeUntil(this._unsubscriber))
            .subscribe(async () => await this._onChainsUpdated());
    }

    ngOnDestroy(): void {
        this._unsubscriber.next();
        this._unsubscriber.complete();
    }

    @ViewChild(DxPopupComponent)
    popup: DxPopupComponent;

    @ViewChild(DxChartComponent)
    chart: DxChartComponent;

    // ── Context (underlying + basis are SEEDED by the opening surface via open()) ──
    underlying: string | null = null;
    basis: HorizontalSkewBasis = 'spot';

    // UI label for the underlying (display name); the wire keeps the raw ticker.
    private _displayLabel: string | null = null;

    // Spot/Forward toggle (dx-button-group) — mirrors the copilot segmented-control pattern.
    readonly basisButtons = [
        {id: 'spot', text: 'Spot'},
        {id: 'forward', text: 'Forward'},
    ];

    // ── Window pickers ──
    startExpiration: string | null = null;
    endExpiration: string | null = null;
    startOptions: WindowOption[] = [];
    endOptions: WindowOption[] = [];

    // ── Curve state (consumed by the template / dx-chart) ──
    isLoading = false;
    hasCurve = false;
    emptyMessage: string | null = null;
    chartData: ChartPoint[] = [];

    private _allOptions: WindowOption[] = [];
    // The loaded curve's as-of date — set only when the backend computed it in QG History
    // replay (curve DTEs/chain describe THAT date's world); shown in the legend so a
    // replayed curve is never mistaken for a live one. Null for live curves.
    private _curveAsOf: string | null = null;

    get basisLabel(): string {
        return this.basis === 'forward' ? 'Forward' : 'Spot';
    }

    get chartTitle(): string {
        return 'Horizontal Skew';
    }

    get legendText(): string {
        const base = `${this._displayLabel || this.underlying || ''} - term structure`;
        const days = this._windowDays();
        let text = days == null ? base : `${base} (${days} ${days === 1 ? 'day' : 'days'})`;
        if (this.hasCurve && this._curveAsOf) {
            const dt = DateTime.fromISO(this._curveAsOf);
            // Platform date format (D17): a replayed curve carries its world's date.
            text += ` @ ${dt.isValid ? dt.toFormat('EEE, dd-MMM-yy') : this._curveAsOf}`;
        }
        return text;
    }

    /** Calendar days spanned by the selected window = difference of the two legs' days-to-expiration. */
    private _windowDays(): number | null {
        const s = this._allOptions.find(o => o.value === this.startExpiration);
        const e = this._allOptions.find(o => o.value === this.endExpiration);
        if (!s || !e) { return null; }
        return Math.abs(e.dte - s.dte);
    }

    // dx-chart calls these with only the info arg — arrow fields keep them binding-safe.
    readonly pctLabel = (info: any): string => {
        const v = info && typeof info.value === 'number' ? info.value : parseFloat(info && info.valueText);
        return (isFinite(v) ? v.toFixed(1) : (info && info.valueText)) + '%';
    };

    readonly tooltipFn = (info: any): { text: string } => {
        const data = info && info.point ? info.point.data : null;
        const dte = data && isValidNumber(data.dte) ? data.dte : null;
        const v = info && typeof info.value === 'number' ? info.value.toFixed(2) : (info && info.valueText);
        const suffix = dte != null ? ` (${dte}d)` : '';
        return {text: `${info && info.argument}${suffix}\n${v}% ATM IV`};
    };

    /** Imperative entry point — the chart button inside each surface's settings overlay. */
    @DetectMethodChanges({isAsync: true})
    async open(underlying: string, basis: HorizontalSkewBasis, displayLabel?: string): Promise<void> {
        this.underlying = underlying;
        this._displayLabel = displayLabel || null;
        this.basis = basis === 'forward' ? 'forward' : 'spot';
        this._resetCurve();
        // Owner decision: open BLANK — the user picks a window and presses Plot explicitly.
        this.emptyMessage = 'Select a window and press Plot.';

        await this._loadExpirations();

        this.popup.visible = true;
    }

    /** User picked a new window start → re-derive the valid end set (and clamp end). */
    @DetectMethodChanges()
    onStartChanged(e: any): void {
        if (!e || !e.event) { return; } // react to USER picks only; skip programmatic sets
        this._recomputeEndOptions();
    }

    /** End-window change → refresh the day-count legend (the decorator runs change detection). */
    @DetectMethodChanges()
    onEndChanged(): void {
        // No state to update — endExpiration is two-way bound; the decorator refreshes legendText.
    }

    /** Spot/Forward toggle — switch the popup's basis and re-plot if a curve is already shown. */
    async onBasisToggle(e: any): Promise<void> {
        const id = e && e.itemData ? e.itemData.id : null;
        if ((id !== 'spot' && id !== 'forward') || id === this.basis) { return; }
        this.basis = id;
        // Plain async (no decorator): _loadCurve() self-renders; the blank branch renders directly.
        if (this.hasCurve) {
            await this._loadCurve();               // re-fetch in the new basis (self-renders + legend)
        } else {
            this._changeDetector.detectChanges();  // blank state: refresh legend + toggle selection
        }
    }

    // Plain async (no @DetectMethodChanges): _loadCurve() runs its own change detection — one pass
    // to show the load panel during the await, one in its finally to render the chart — so decorating
    // here would only add a redundant third detectChanges after the promise resolves.
    async onPlot(): Promise<void> {
        await this._loadCurve();
    }

    /** Restore the auto visual range after the user has mouse-zoomed/panned. */
    @DetectMethodChanges()
    resetZoom(): void {
        if (this.chart && this.chart.instance) {
            this.chart.instance.resetVisualRange();
        }
    }

    @DetectMethodChanges()
    onHidden(): void {
        this.popup.visible = false;
    }

    close(): void {
        this.onHidden();
    }

    // ── internals ───────────────────────────────────────────────────────────

    private async _loadExpirations(preserveSelection: boolean = false): Promise<void> {
        const prevStart = preserveSelection ? this.startExpiration : null;
        const prevEnd = preserveSelection ? this.endExpiration : null;

        this._allOptions = [];
        this.startOptions = [];
        this.endOptions = [];
        if (!this.underlying) { return; }

        // ETS-standard expiration source (same pattern as the OPG expiration selector): the
        // frontend option chains are themselves adjusted on QG cursor moves, so their dates,
        // display strings, and day counts already describe the world being quoted.
        const chain = await this._optionChains.getChain(this.underlying);
        const exps = chain && chain.expirations ? chain.expirations : [];

        const opts: WindowOption[] = [];
        exps.forEach((e: any) => {
            const dte = isValidNumber(e.daysToExpiration) ? e.daysToExpiration : daysToExpiration(e.optionExpirationDate);
            if (!isValidNumber(dte) || dte < 0) { return; } // skip expired / bad rows
            opts.push({
                value: e.optionExpirationDate,
                label: e.dateWithDaysToExpiration || e.optionExpirationDate,
                short: e.optionExpirationDisplayDate || e.optionExpirationDate,
                dte
            });
        });
        opts.sort((a, b) => a.dte - b.dte);

        this._allOptions = opts;
        this.startOptions = opts.slice();

        if (opts.length === 0) { return; }

        // On a reference-data refresh keep the user's window when it still exists in the new
        // world; otherwise fall back to the default (nearest → farthest inside +120d).
        if (prevStart && opts.some(o => o.value === prevStart)) {
            this.startExpiration = prevStart;
            this._recomputeEndOptions(); // keeps prevEnd when still valid, clamps otherwise
            if (prevEnd && this.endOptions.some(o => o.value === prevEnd)) {
                this.endExpiration = prevEnd;
            }
            return;
        }

        // Default window: nearest non-expired expiration → the farthest one still inside +120d.
        this.startExpiration = opts[0].value;
        this._recomputeEndOptions();
        if (this.endOptions.length > 0) {
            this.endExpiration = this.endOptions[this.endOptions.length - 1].value;
        }
    }

    /** 'Qg.ChainsUpdatedMessage': the world's date moved — re-bind an OPEN popup's pickers. */
    private async _onChainsUpdated(): Promise<void> {
        if (!this.popup || !this.popup.visible || !this.underlying) { return; }
        await this._loadExpirations(/*preserveSelection*/ true);
        this._changeDetector.detectChanges();
    }

    /** End options = expirations at/after start and within the +120d cap; clamp end into it. */
    private _recomputeEndOptions(): void {
        const start = this._allOptions.find(o => o.value === this.startExpiration);
        if (!start) {
            this.endOptions = this._allOptions.slice();
            return;
        }
        this.endOptions = this._allOptions.filter(
            o => o.dte >= start.dte && (o.dte - start.dte) <= CURVE_MAX_DAYS);

        if (!this.endOptions.some(o => o.value === this.endExpiration)) {
            this.endExpiration = this.endOptions.length > 0
                ? this.endOptions[this.endOptions.length - 1].value
                : this.startExpiration;
        }
    }

    private async _loadCurve(): Promise<void> {
        if (!this.underlying || !this.startExpiration || !this.endExpiration) { return; }

        this.isLoading = true;
        this._changeDetector.detectChanges();

        try {
            const qry = new GetHorizontalSkewCurve(
                this.underlying, this.basis, this.startExpiration, this.endExpiration);

            if (HS_TRACE) {
                console.log('[HS] chart request ul=%s basis=%s window=[%s..%s]',
                    this.underlying, this.basis, this.startExpiration, this.endExpiration);
            }

            const reply = await this._shellClient.processQuery<GetHorizontalSkewCurveReply>(qry);
            this._curveAsOf = (reply && reply.asOfDate) || null;
            // Guard: the QH returns Points=[] on any failure, and an empty repeated field can
            // collapse to null across serialization — never call array ops on a bare reply.
            const raw = (reply && reply.points) || [];
            this._ingestCurve(raw);
        } catch (e) {
            console.error('[HS] chart curve load failed', e);
            this._resetCurve();
            this.emptyMessage = 'Failed to load curve.';
        } finally {
            this.isLoading = false;
            this._changeDetector.detectChanges();
        }
    }

    private _ingestCurve(raw: HorizontalSkewCurvePoint[]): void {
        // Keep only points with a usable IV — a missing-IV expiration is skipped, never
        // fabricated (locked decision). The backend already orders points ascending by date.
        const usable = raw.filter(p => p && p.iv != null && isValidNumber(p.iv));

        this.chartData = usable.map(p => {
            const o = this._allOptions.find(x => x.value === p.expiration);
            // Fallback for points missing from the picker set: measure from the curve's own
            // as-of date in replay (D19) — the live clock only when the curve IS live.
            const dte = o ? o.dte : this._fallbackDte(p.expiration);
            return {
                exp: p.expiration,
                expLabel: o ? o.short : p.expiration,
                dte,
                ivPct: (p.iv as number) * 100
            };
        });

        const skipped = raw.length - usable.length;
        if (HS_TRACE && skipped > 0) {
            console.log('[HS] chart: skipped %s missing-IV point(s) of %s', skipped, raw.length);
        }

        this.hasCurve = this.chartData.length > 0;
        this.emptyMessage = this.hasCurve ? null : 'No IV available in this window.';
    }

    private _fallbackDte(expiration: string): number {
        if (this._curveAsOf) {
            const d = DateTime.fromISO(expiration).diff(DateTime.fromISO(this._curveAsOf), 'days').days;
            return isValidNumber(d) ? Math.round(d) : 0;
        }
        const live = daysToExpiration(expiration);
        return isValidNumber(live) ? live : 0;
    }

    private _resetCurve(): void {
        this.chartData = [];
        this.hasCurve = false;
        this.emptyMessage = null;
        this._curveAsOf = null;
    }
}
